import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Layers, Receipt, Calculator, TrendingUp, Sparkles, Users } from "lucide-react";
import type { ReactNode } from "react";

interface BentoCard {
    iconNode: ReactNode;
    titleKey: string;
    descKey: string;
    visual: ReactNode | null;
    span: string;
    accent: "primary" | "purple" | "blue" | "green" | "amber" | "rose";
}

const ACCENT_CLASSES: Record<BentoCard["accent"], { bg: string; text: string }> = {
    primary: { bg: "bg-accent-primary/10", text: "text-accent-primary" },
    purple: { bg: "bg-purple-100 dark:bg-purple-500/10", text: "text-purple-600 dark:text-purple-400" },
    blue: { bg: "bg-blue-100 dark:bg-blue-500/10", text: "text-blue-600 dark:text-blue-400" },
    green: { bg: "bg-success-50 dark:bg-success-500/10", text: "text-success-600 dark:text-success-500" },
    amber: { bg: "bg-warning-50 dark:bg-warning-500/10", text: "text-warning-600 dark:text-warning-500" },
    rose: { bg: "bg-rose-100 dark:bg-rose-500/10", text: "text-rose-600 dark:text-rose-400" },
};

const ChannelsVisual = () => (
    <div className="absolute inset-x-6 bottom-6 h-28 flex items-end gap-2">
        {[40, 60, 35, 75, 50, 85, 65].map((h, i) => (
            <div key={i} className="flex-1 rounded-t-md bg-gradient-to-t from-accent-primary/20 to-accent-primary/60" style={{ height: `${h}%` }} />
        ))}
    </div>
);

const JournalVisual = () => {
    const { t } = useTranslation("common");
    const items = [
        { labelKey: "landing.feature.visuals.journalLabel1", valueKey: "landing.feature.visuals.journalValue1" },
        { labelKey: "landing.feature.visuals.journalLabel2", valueKey: "landing.feature.visuals.journalValue2" },
        { labelKey: "landing.feature.visuals.journalLabel3", valueKey: "landing.feature.visuals.journalValue3" },
    ];
    return (
        <div className="absolute inset-x-6 bottom-6 space-y-1.5">
            {items.map((item) => (
                <div key={item.labelKey} className="flex items-center justify-between rounded-md bg-white/80 dark:bg-neutral-800/60 border border-neutral-200/80 dark:border-neutral-700/80 px-3 py-1.5 text-xs">
                    <span className="font-medium text-text-primary">{t(item.labelKey)}</span>
                    <span className="text-rose-500 dark:text-rose-400 tabular-nums">{t(item.valueKey)}</span>
                </div>
            ))}
        </div>
    );
};

const FormulaVisual = () => {
    const { t } = useTranslation("common");
    return (
        <div className="absolute inset-x-6 bottom-6 flex flex-wrap items-center gap-1.5">
            <span className="px-2.5 py-1 rounded-md bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-500 text-[11px] font-semibold border border-success-100 dark:border-success-500/20">
                {t("landing.feature.visuals.formulaSales")}
            </span>
            <span className="text-neutral-400 text-sm">−</span>
            <span className="px-2.5 py-1 rounded-md bg-rose-100 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 text-[11px] font-semibold border border-rose-200/60 dark:border-rose-500/20">
                {t("landing.feature.visuals.formulaCosts")}
            </span>
            <span className="text-neutral-400 text-sm">÷</span>
            <span className="px-2.5 py-1 rounded-md bg-blue-100 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 text-[11px] font-semibold border border-blue-200/60 dark:border-blue-500/20">
                {t("landing.feature.visuals.formulaOrders")}
            </span>
            <span className="text-neutral-400 text-sm">=</span>
            <span className="px-2.5 py-1 rounded-md bg-accent-primary/10 text-accent-primary text-[11px] font-bold border border-accent-primary/20">
                {t("landing.feature.visuals.formulaMargin")}
            </span>
        </div>
    );
};

const ForecastVisual = () => (
    <div className="absolute inset-x-6 bottom-6 h-20">
        <svg viewBox="0 0 200 60" className="w-full h-full" preserveAspectRatio="none">
            <defs>
                <linearGradient id="forecastFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#c7613d" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#c7613d" stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d="M0,40 L30,35 L60,28 L90,30 L120,22" stroke="#c7613d" strokeWidth="2" fill="none" />
            <path d="M120,22 L150,18 L180,12 L200,8" stroke="#c7613d" strokeWidth="2" strokeDasharray="3 3" fill="none" />
            <path d="M0,40 L30,35 L60,28 L90,30 L120,22 L150,18 L180,12 L200,8 L200,60 L0,60 Z" fill="url(#forecastFill)" />
        </svg>
    </div>
);

const AiVisual = ({ question, answer }: { question: string; answer: string }) => (
    <div className="absolute inset-x-6 bottom-6 space-y-2">
        <div className="rounded-md bg-neutral-100 dark:bg-neutral-800 px-3 py-2 text-xs text-text-primary border border-neutral-200 dark:border-neutral-700 inline-block">
            {question}
        </div>
        <div className="rounded-md bg-accent-primary/10 border border-accent-primary/20 px-3 py-2 text-xs text-text-primary">
            {answer}
        </div>
    </div>
);

const ShareVisual = () => (
    <div className="absolute inset-x-6 bottom-6 flex items-center gap-3">
        <div className="flex -space-x-2">
            {["A", "M", "S"].map((c, i) => (
                <div key={i} className={`w-9 h-9 rounded-full border-2 border-white dark:border-neutral-900 flex items-center justify-center text-xs font-semibold text-white ${["bg-accent-primary", "bg-purple-500", "bg-blue-500"][i]}`}>
                    {c}
                </div>
            ))}
        </div>
        <div className="rounded-md bg-white/80 dark:bg-neutral-800/60 border border-neutral-200/80 dark:border-neutral-700/80 px-2.5 py-1 text-[10px] font-mono text-neutral-500">
            EUR · USD · CAD
        </div>
    </div>
);

const CARDS: BentoCard[] = [
    {
        iconNode: <Layers size={18} />,
        titleKey: "landing.feature.channelsTitle",
        descKey: "landing.feature.channelsDesc",
        visual: <ChannelsVisual />,
        span: "lg:col-span-2 lg:row-span-1",
        accent: "primary",
    },
    {
        iconNode: <Receipt size={18} />,
        titleKey: "landing.feature.journalsTitle",
        descKey: "landing.feature.journalsDesc",
        visual: <JournalVisual />,
        span: "",
        accent: "rose",
    },
    {
        iconNode: <Calculator size={18} />,
        titleKey: "landing.feature.formulasTitle",
        descKey: "landing.feature.formulasDesc",
        visual: <FormulaVisual />,
        span: "",
        accent: "blue",
    },
    {
        iconNode: <TrendingUp size={18} />,
        titleKey: "landing.feature.forecastTitle",
        descKey: "landing.feature.forecastDesc",
        visual: <ForecastVisual />,
        span: "",
        accent: "green",
    },
    {
        iconNode: <Sparkles size={18} />,
        titleKey: "landing.feature.aiTitle",
        descKey: "landing.feature.aiDesc",
        visual: null,
        span: "lg:col-span-2",
        accent: "purple",
    },
    {
        iconNode: <Users size={18} />,
        titleKey: "landing.feature.sharingTitle",
        descKey: "landing.feature.sharingDesc",
        visual: <ShareVisual />,
        span: "",
        accent: "amber",
    },
];

export const FeatureBento = () => {
    const { t } = useTranslation("common");

    return (
        <section id="features" className="py-20 sm:py-28">
            <div className="container mx-auto px-4 sm:px-6">
                <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
                    <p className="text-xs font-semibold tracking-[0.2em] uppercase text-accent-primary mb-3">
                        {t("landing.feature.eyebrow")}
                    </p>
                    <h2 className="text-3xl sm:text-4xl font-bold font-display text-text-primary mb-4">
                        {t("landing.feature.title")}
                    </h2>
                    <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400">
                        {t("landing.feature.subtitle")}
                    </p>
                </div>

                <div className="grid lg:grid-cols-3 gap-4 sm:gap-5 auto-rows-[340px]">
                    {CARDS.map((card, i) => {
                        const accent = ACCENT_CLASSES[card.accent];
                        return (
                            <motion.div
                                key={card.titleKey}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.4, delay: (i % 3) * 0.07 }}
                                className={`relative overflow-hidden rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:border-accent-primary/40 transition-colors ${card.span}`}
                            >
                                <div className="p-6 sm:p-7 pb-36">
                                    <div className={`inline-flex w-10 h-10 rounded-lg items-center justify-center mb-4 ${accent.bg} ${accent.text}`}>
                                        {card.iconNode}
                                    </div>
                                    <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-2">
                                        {t(card.titleKey)}
                                    </h3>
                                    <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-md line-clamp-3">
                                        {t(card.descKey)}
                                    </p>
                                </div>
                                {card.titleKey === "landing.feature.aiTitle" ? (
                                    <AiVisual
                                        question={t("landing.feature.aiQuestion") as string}
                                        answer={t("landing.feature.aiAnswer") as string}
                                    />
                                ) : (
                                    card.visual
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
